import { ReactNode } from 'react';
import { LucideIcon, ArrowUp, ArrowDown } from 'lucide-react';
import { ADMIN_RADII, ADMIN_TYPE } from './tokens';

interface AdminStatTileProps {
  label: ReactNode;
  value: ReactNode;
  /** Optional small icon shown next to the label. */
  icon?: LucideIcon;
  /** Optional delta (e.g. "+12% vs last month"). Positive = green, negative = rose. */
  delta?: {
    value: string;
    direction: 'up' | 'down' | 'neutral';
  };
  /** Optional small hint line below the value. */
  hint?: ReactNode;
  /** Skeleton state for loading. */
  loading?: boolean;
  className?: string;
}

/**
 * Stat tile for the Dashboard.
 *
 * Convention from Stripe Dashboard / Linear Insights:
 *  - Overline-style label, tabular-nums value, optional ±% delta
 *  - Plain white card, monochrome — color reserved for delta indicator
 *  - 44px+ height even when empty so grid stays uniform
 */
export function AdminStatTile({
  label,
  value,
  icon: Icon,
  delta,
  hint,
  loading = false,
  className = '',
}: AdminStatTileProps) {
  return (
    <div
      className={`border border-gray-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)] sm:p-5 ${ADMIN_RADII.card} ${className}`}
    >
      <div className="flex items-center gap-2">
        {Icon && (
          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-700">
            <Icon className="h-3.5 w-3.5" strokeWidth={2} />
          </span>
        )}
        <span className={ADMIN_TYPE.statLabel}>{label}</span>
      </div>

      {loading ? (
        <div className="mt-3 h-8 w-24 animate-pulse rounded bg-gray-100" />
      ) : (
        <div className={`mt-3 ${ADMIN_TYPE.statValue} text-gray-950`}>{value}</div>
      )}

      {(delta || hint) && (
        <div className="mt-2 flex items-center gap-2 text-xs">
          {delta && (
            <span
              className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-medium tabular-nums ${
                delta.direction === 'up'
                  ? 'bg-green-50 text-green-700'
                  : delta.direction === 'down'
                    ? 'bg-rose-50 text-rose-700'
                    : 'bg-gray-100 text-gray-700'
              }`}
            >
              {delta.direction === 'up' && (
                <ArrowUp className="h-3 w-3" strokeWidth={2.5} />
              )}
              {delta.direction === 'down' && (
                <ArrowDown className="h-3 w-3" strokeWidth={2.5} />
              )}
              {delta.value}
            </span>
          )}
          {hint && <span className="text-gray-500">{hint}</span>}
        </div>
      )}
    </div>
  );
}
